/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Avatar, Divider, Grid, Typography, Typography2 } from '@coursera/cds-core';

import SocialPlatformLink from 'bundles/shared-articles/components/SocialPlatformLink';
import { getSocialPlatformInfo, validateExternalUrl } from 'bundles/shared-articles/utils';
import type { Maybe } from 'bundles/unified-seo-content-common/__generated__/baseContentfulTypes';

export type Locale = {
  language: string;
  country: string;
};

export type KnowsAbout = {
  title: string;
  slug: string;
  locale: Locale;
};

export type AuthorProfile = {
  name?: Maybe<string>;
  url?: Maybe<string>;
  jobTitle?: Maybe<string>;
  externalWebsites?: Maybe<Maybe<string>[]>;
  description?: Maybe<string>;
  knowsAbout?: KnowsAbout[];
  image?: Maybe<string>;
};

const styles = {
  divider: css`
    margin: var(--cds-spacing-200) 0;
  `,
  nameWrapper: css`
    flex-grow: 1;
    flex-shrink: 0;
  `,
  socialLinks: css`
    list-style: none;
    font-size: var(--cds-font-size-175);

    li a {
      font-size: var(--cds-font-size-175);
    }
  `,
};

const ArticleAuthorProfile = ({ name, description, jobTitle, image, externalWebsites }: AuthorProfile) => {
  if (!name) {
    return null;
  }
  const imageProps = (image && { src: image, alt: name }) || undefined;

  return (
    <Grid container spacing={16}>
      <Grid item>
        <Avatar variant="user" size={200} showBorder imgProps={imageProps} data-testid="author-avatar" />
      </Grid>
      <Grid item css={styles.nameWrapper}>
        <Typography variant="h1semibold">{name}</Typography>
        {jobTitle && (
          <Typography variant="h2" color="supportText">
            {jobTitle}
          </Typography>
        )}
      </Grid>
      {!!externalWebsites?.length && (
        <Grid item xs={12} md={4} lg={3} component="ul" css={styles.socialLinks}>
          {externalWebsites.map((url) => {
            if (!url || !validateExternalUrl(url)) {
              return null;
            }
            const { displayUrl, socialPlatform } = getSocialPlatformInfo(url);
            return (
              <li key={url}>
                <SocialPlatformLink url={url} displayUrl={displayUrl} socialPlatform={socialPlatform} />
              </li>
            );
          })}
        </Grid>
      )}
      {description && (
        <Grid item xs={12}>
          <Divider css={styles.divider} />
          <Typography variant="h2semibold">Bio</Typography>
          <Typography2 component="p" variant="bodyPrimary">
            {description}
          </Typography2>
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider css={styles.divider} />
      </Grid>
    </Grid>
  );
};

export default ArticleAuthorProfile;
